// v3.7.0 — Preuve de Livraison "cockpit Mercedes" : caméra live + PIN néon +
// écran de succès animé. Route /courier/arrival/:order_ref.
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const NEON = "#00FF88";

async function _compressImage(file, maxWidth = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

// ── Caméra live (fallback upload fichier si refusée/indisponible) ────────────
function CameraCapture({ onCapture, onClose, onFallbackFile }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 } }, audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (_) { if (!cancelled) setError(true); }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    const canvas = document.createElement("canvas");
    const w = Math.min(1600, v.videoWidth || 1280);
    const ratio = w / (v.videoWidth || 1280);
    canvas.width = w; canvas.height = Math.round((v.videoHeight || 720) * ratio);
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    onCapture(canvas.toDataURL("image/jpeg", 0.85));
  };

  return (
    <div data-testid="camera-overlay" style={camOverlayStyle}>
      <div style={{ position: "relative", width: "100%", maxWidth: 520, borderRadius: 18, overflow: "hidden", border: `1px solid rgba(0,255,136,0.35)`, boxShadow: `0 0 40px rgba(0,255,136,0.12)` }}>
        {!error ? (
          <>
            <video ref={videoRef} playsInline muted style={{ width: "100%", display: "block", background: "#000", minHeight: 280 }} />
            {/* Cadre viseur */}
            <div style={{ position: "absolute", inset: 14, border: "1.5px dashed rgba(0,255,136,0.45)", borderRadius: 12, pointerEvents: "none" }} />
            {!ready && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#8FA3A3", fontSize: 13 }}>Ouverture caméra…</div>}
          </>
        ) : (
          <div style={{ padding: "36px 20px", textAlign: "center", background: "#0A1210" }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>📵</div>
            <div style={{ fontSize: 13, color: "#8FA3A3", lineHeight: 1.5 }}>Caméra indisponible ou refusée.<br />Utilise l&apos;upload de fichier.</div>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 18, alignItems: "center" }}>
        <button onClick={onClose} data-testid="camera-cancel" style={camGhostBtnStyle}>Annuler</button>
        {!error && (
          <button onClick={snap} disabled={!ready} data-testid="camera-shutter" aria-label="Capturer" style={{ ...shutterStyle, opacity: ready ? 1 : 0.4 }}>
            <span style={shutterInnerStyle} />
          </button>
        )}
        <button onClick={() => fileRef.current?.click()} data-testid="camera-file-fallback" style={camGhostBtnStyle}>📁 Fichier</button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFallbackFile(f); }} />
    </div>
  );
}

// ── Écran de succès animé ────────────────────────────────────────────────────
function SuccessOverlay({ share, orderRef, demoTip = null, demoMode = false }) {
  return (
    <div data-testid="delivery-success-overlay" style={successOverlayStyle}>
      <style>{`
        @keyframes aya-pop { 0% { transform: scale(0); } 60% { transform: scale(1.15); } 100% { transform: scale(1); } }
        @keyframes aya-ring { 0% { transform: scale(0.6); opacity: 0.8; } 100% { transform: scale(2.2); opacity: 0; } }
        @keyframes aya-draw { to { stroke-dashoffset: 0; } }
        @keyframes aya-rise { 0% { transform: translateY(16px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
        @keyframes aya-confetti { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(-90px) rotate(200deg); opacity: 0; } }
      `}</style>
      <div style={{ position: "relative", width: 130, height: 130, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {[0, 0.35, 0.7].map((d, i) => (
          <span key={i} style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${NEON}`, animation: `aya-ring 1.4s ease-out ${d}s infinite` }} />
        ))}
        <div style={{ width: 110, height: 110, borderRadius: "50%", background: "rgba(0,255,136,0.12)", border: `2px solid ${NEON}`, display: "flex", alignItems: "center", justifyContent: "center", animation: "aya-pop 0.5s cubic-bezier(0.34,1.56,0.64,1)", boxShadow: `0 0 44px rgba(0,255,136,0.35)` }}>
          <svg width="56" height="56" viewBox="0 0 52 52">
            <path fill="none" stroke={NEON} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"
              d="M14 27 L23 36 L39 17"
              style={{ strokeDasharray: 48, strokeDashoffset: 48, animation: "aya-draw 0.45s ease-out 0.35s forwards" }} />
          </svg>
        </div>
        {["🎉", "✨", "💚", "🎊", "✨", "🎉"].map((c, i) => (
          <span key={`c${i}`} style={{
            position: "absolute", left: `${8 + i * 20}%`, top: "58%", fontSize: 17,
            animation: `aya-confetti 1.3s ease-out ${0.4 + i * 0.14}s infinite`,
          }}>{c}</span>
        ))}
      </div>
      <div style={{ marginTop: 26, textAlign: "center", animation: "aya-rise 0.5s ease-out 0.5s both" }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#EAFDF3", letterSpacing: -0.3 }}>Livraison confirmée !</div>
        <div style={{ fontFamily: "monospace", fontSize: 12, color: "#8FA3A3", marginTop: 6 }}>{orderRef}</div>
        {Number.isFinite(Number(share)) && (
          <div style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 999, background: "rgba(0,255,136,0.10)", border: `1px solid rgba(0,255,136,0.4)` }}>
            <span style={{ fontSize: 13, color: "#8FA3A3" }}>Ta part</span>
            <span style={{ fontSize: 18, fontWeight: 900, color: NEON, fontFeatureSettings: "'tnum'" }}>+{Number(share).toLocaleString("fr-FR")} FCFA</span>
          </div>
        )}
        {/* v3.20.0 — Pourboire client (démo) : le mécanisme note+pourboire du chat, visible côté livreur. */}
        {Number.isFinite(Number(demoTip)) && demoTip > 0 && (
          <div data-testid="delivery-demo-tip" style={{
            marginTop: 10, display: "inline-flex", alignItems: "center", gap: 8,
            padding: "10px 20px", borderRadius: 999,
            background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.45)",
            animation: "aya-rise 0.5s ease-out both",
          }}>
            <span style={{ fontSize: 13, color: "#8FA3A3" }}>💚 Pourboire client (⭐ 5/5)</span>
            <span style={{ fontSize: 18, fontWeight: 900, color: "#FBBF24", fontFeatureSettings: "'tnum'" }}>+{Number(demoTip).toLocaleString("fr-FR")} FCFA</span>
          </div>
        )}
        <div style={{ marginTop: 14, fontSize: 12, color: "#8FA3A3" }}>{demoMode ? "Fin de la démo — retour au GPS…" : "Redirection vers le replay…"}</div>
      </div>
    </div>
  );
}

const EXTRA_SLOTS = [
  { k: "address_facade", label: "Façade / n° porte", icon: "🏠" },
  { k: "street_context", label: "Rue / contexte", icon: "🛣️" },
];

export default function CourierArrivalPage() {
  const { order_ref } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState({ address_facade: null, street_context: null, delivery_proof: null });
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  // v3.20.0 — Mode DÉMO (order_ref DEMO-*) : preuve de livraison + pourboire
  // simulés, aucun appel backend — enchaîne depuis "▶ Démo livraison Awa".
  const isDemo = String(order_ref || "").toUpperCase().startsWith("DEMO-");
  const [demoTip, setDemoTip] = useState(null);
  const inputRefs = useRef({});
  const pinRef = useRef(null);

  useEffect(() => {
    if (isDemo) {
      setOrder({
        order_ref,
        commerce_name: "Boutique Awa Cosmétiques (Cocody)",
        drop: { description: "Client Riviera — CIV-COC-RIV-DEMO1" },
        client_name_masked: "M. K***é",
        courier_share_fcfa: 800,
      });
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/v3/delivery/my-courses`, { credentials: "include" });
        const d = await r.json();
        if (r.ok && d.ok) {
          const found = (d.courses || []).find((c) => c.order_ref === order_ref);
          if (found) setOrder(found);
          else setError("Course introuvable pour ce livreur");
        } else setError(d.error || `HTTP ${r.status}`);
      } catch (_) { setError("network_error"); }
      finally { setLoading(false); }
    })();
  }, [order_ref]);

  const onFile = useCallback(async (slot, file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("Photo > 12 Mo"); return; }
    try {
      const dataUrl = await _compressImage(file);
      setPhotos((p) => ({ ...p, [slot]: dataUrl }));
      if (slot === "delivery_proof") setCameraOpen(false);
    } catch (_) { toast.error("Compression échouée"); }
  }, []);

  const submit = useCallback(async () => {
    if (!photos.delivery_proof) { toast.error("Photo de preuve obligatoire"); return; }
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN client (4 chiffres) requis"); return; }
    // v3.20.0 — Finalisation DÉMO : PIN 1234, succès + pourboire simulés.
    if (isDemo) {
      if (pin !== "1234") { toast.error("PIN client incorrect (démo : 1234)"); return; }
      setSubmitting(true);
      await new Promise((r) => setTimeout(r, 900));
      setSuccess(true);
      try { if (navigator.vibrate) navigator.vibrate([80, 40, 160]); } catch (_) {}
      setTimeout(() => setDemoTip(500), 1500);
      setTimeout(() => { window.location.href = "/livreur/navigate"; }, 6000);
      setSubmitting(false);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        pin_code: pin,
        delivery_proof_base64: photos.delivery_proof,
        address_facade_base64: photos.address_facade,
        street_context_base64: photos.street_context,
      };
      const r = await fetch(`${API}/v3/delivery/orders/${order_ref}/finalize`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        setSuccess(true);
        try { if (navigator.vibrate) navigator.vibrate([80, 40, 160]); } catch (_) {}
        setTimeout(() => navigate(`/replay/${order_ref}`), 2800);
      } else if (d.error === "invalid_client_pin") {
        toast.error("PIN client incorrect");
      } else {
        toast.error(d.error || `Erreur ${r.status}`);
      }
    } catch (_) { toast.error("Erreur réseau"); }
    finally { setSubmitting(false); }
  }, [photos, pin, order_ref, navigate, isDemo]);

  if (loading) return <div style={pageStyle}><div style={{ padding: 24, color: "#8FA3A3" }}>Chargement…</div></div>;
  if (error || !order) {
    return <div style={pageStyle}><div style={cardStyle}>
      <h1 style={{ color: "#F87171", fontSize: 20, margin: 0 }}>Impossible de finaliser</h1>
      <p style={{ color: "#8FA3A3", marginTop: 10, fontSize: 13 }}>{error || "Course inaccessible"}</p>
      <Link to="/livreur/navigate" style={ctaAltStyle}>← Retour navigation</Link>
    </div></div>;
  }

  const pinDigits = [0, 1, 2, 3].map((i) => pin[i] || "");
  const canSubmit = !!photos.delivery_proof && /^\d{4}$/.test(pin);

  return (
    <div style={pageStyle} data-testid="courier-arrival-page">
      {success && <SuccessOverlay share={order.courier_share_fcfa} orderRef={order_ref} demoTip={demoTip} demoMode={isDemo} />}
      {cameraOpen && (
        <CameraCapture
          onCapture={(dataUrl) => { setPhotos((p) => ({ ...p, delivery_proof: dataUrl })); setCameraOpen(false); }}
          onClose={() => setCameraOpen(false)}
          onFallbackFile={(f) => onFile("delivery_proof", f)}
        />
      )}

      <header style={headerStyle}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: NEON, boxShadow: `0 0 10px ${NEON}` }} />
          <span style={{ fontSize: 11, color: NEON, fontWeight: 800, letterSpacing: 2 }}>PREUVE DE LIVRAISON</span>
        </div>
        <div style={{ fontFamily: "monospace", fontSize: 13, color: "#8FA3A3", marginTop: 5 }}>{order.order_ref}</div>
      </header>

      {/* Récap course */}
      <section style={{ ...cardStyle, marginTop: 14 }} data-testid="arrival-recap-card">
        <div style={recapRowStyle}><span style={kStyle}>📦 Pickup</span><span style={vStyle}>{order.commerce_name}</span></div>
        <div style={recapRowStyle}><span style={kStyle}>🏁 Drop</span><span style={vStyle}>{order.drop?.description || order.drop?.address_civ}</span></div>
        <div style={recapRowStyle}><span style={kStyle}>👤 Client</span><span style={vStyle}>{order.client_name_masked || "—"}</span></div>
        <div style={{ ...recapRowStyle, borderBottom: "none" }}>
          <span style={kStyle}>💰 Ta part</span>
          <span style={{ ...vStyle, color: NEON, fontWeight: 800 }}>{order.courier_share_fcfa} FCFA</span>
        </div>
      </section>

      {/* Photo preuve — caméra live */}
      <section style={{ ...cardStyle, marginTop: 12 }}>
        <div style={sectionTitleStyle}>📸 Photo du colis remis <span style={{ color: "#F87171" }}>*</span></div>
        <input
          ref={(el) => { inputRefs.current.delivery_proof = el; }}
          type="file" accept="image/*" capture="environment"
          onChange={(e) => onFile("delivery_proof", e.target.files?.[0])}
          data-testid="arrival-photo-delivery_proof"
          style={{ display: "none" }}
        />
        {!photos.delivery_proof ? (
          <button onClick={() => setCameraOpen(true)} data-testid="arrival-photo-btn-delivery_proof" style={cameraBtnStyle}>
            <span style={{ fontSize: 24 }}>📷</span>
            <span>Ouvrir la caméra</span>
            <span style={{ fontSize: 11, color: "#8FA3A3", fontWeight: 500 }}>Capture le colis entre les mains du client</span>
          </button>
        ) : (
          <div style={{ position: "relative" }}>
            <img src={photos.delivery_proof} alt="preuve" data-testid="arrival-photo-preview-delivery_proof" style={photoPreviewStyle} />
            <span style={proofBadgeStyle}>✓ Capturée</span>
            <button onClick={() => setCameraOpen(true)} style={photoRetakeStyle} data-testid="arrival-photo-retake-delivery_proof">🔄 Reprendre</button>
          </div>
        )}

        {/* Slots optionnels compacts */}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          {EXTRA_SLOTS.map((s) => (
            <div key={s.k} style={{ flex: 1 }}>
              <input
                ref={(el) => { inputRefs.current[s.k] = el; }}
                type="file" accept="image/*" capture="environment"
                onChange={(e) => onFile(s.k, e.target.files?.[0])}
                data-testid={`arrival-photo-${s.k}`}
                style={{ display: "none" }}
              />
              {!photos[s.k] ? (
                <button onClick={() => inputRefs.current[s.k]?.click()} data-testid={`arrival-photo-btn-${s.k}`} style={extraSlotBtnStyle}>
                  <span>{s.icon}</span>
                  <span style={{ fontSize: 10.5 }}>{s.label}</span>
                  <span style={{ fontSize: 9, color: "#5C6E6E" }}>optionnel</span>
                </button>
              ) : (
                <div style={{ position: "relative" }}>
                  <img src={photos[s.k]} alt={s.k} data-testid={`arrival-photo-preview-${s.k}`} style={{ width: "100%", height: 74, objectFit: "cover", borderRadius: 10, border: "1px solid rgba(0,255,136,0.35)" }} />
                  <button onClick={() => inputRefs.current[s.k]?.click()} style={{ ...photoRetakeStyle, position: "absolute", bottom: 4, right: 4, padding: "3px 8px", fontSize: 9 }}>🔄</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* PIN 4 chiffres */}
      <section style={{ ...cardStyle, marginTop: 12 }}>
        <div style={sectionTitleStyle}>🔑 Code PIN du client</div>
        <div style={{ position: "relative", marginTop: 4 }} onClick={() => pinRef.current?.focus()}>
          <input
            ref={pinRef}
            type="tel" inputMode="numeric" maxLength={4} autoComplete="one-time-code"
            value={pin} onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
            data-testid="arrival-pin-input"
            style={hiddenPinInputStyle}
          />
          {isDemo && (
            <div data-testid="arrival-demo-pin-hint" style={{ textAlign: "center", fontSize: 11.5, color: "#FBBF24", marginBottom: 8 }}>
              🧪 Mode démo — PIN client : <strong>1234</strong>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            {pinDigits.map((d, i) => {
              const isActive = i === Math.min(pin.length, 3);
              return (
                <div key={i} data-testid={`arrival-pin-box-${i}`} style={{
                  width: 56, height: 64, borderRadius: 14,
                  background: d ? "rgba(0,255,136,0.08)" : "rgba(255,255,255,0.03)",
                  border: d ? `1.5px solid ${NEON}` : isActive ? "1.5px solid rgba(0,255,136,0.5)" : "1px solid rgba(255,255,255,0.12)",
                  boxShadow: d ? "0 0 14px rgba(0,255,136,0.18)" : "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 26, fontWeight: 900, fontFamily: "monospace", color: d ? NEON : "#5C6E6E",
                  transition: "border-color 150ms ease, box-shadow 150ms ease",
                }}>{d || (isActive ? "·" : "")}</div>
              );
            })}
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#5C6E6E", marginTop: 10, textAlign: "center" }}>Le client a reçu son code à la commande — demande-le lui.</div>
      </section>

      <div style={{ padding: "16px 14px" }}>
        <button onClick={submit} disabled={submitting || !canSubmit} data-testid="arrival-submit"
          style={{ ...primaryBtnStyle, opacity: (submitting || !canSubmit) ? 0.35 : 1 }}>
          {submitting ? "Validation…" : "✅ Finaliser la livraison"}
        </button>
        <Link to="/livreur/navigate" data-testid="arrival-back" style={{ ...ctaAltStyle, display: "block", textAlign: "center", marginTop: 12 }}>← Retour navigation</Link>
      </div>
    </div>
  );
}

// ── Styles cockpit ───────────────────────────────────────────────────────────
const pageStyle = { minHeight: "100vh", background: "radial-gradient(ellipse at top, #0D1A16 0%, #04090B 65%)", color: "#EAFDF3", fontFamily: "Inter, system-ui, sans-serif", paddingBottom: 30 };
const headerStyle = { padding: "18px 16px 14px", textAlign: "center", borderBottom: "1px solid rgba(0,255,136,0.12)" };
const cardStyle = { background: "linear-gradient(180deg, rgba(13,18,22,0.85), rgba(5,9,11,0.9))", border: "1px solid rgba(0,255,136,0.16)", borderRadius: 16, padding: 16, margin: "0 14px", backdropFilter: "blur(10px)", boxShadow: "0 8px 24px rgba(0,0,0,0.45)" };
const sectionTitleStyle = { fontSize: 12, color: NEON, fontWeight: 800, letterSpacing: 1, marginBottom: 12, textTransform: "uppercase" };
const recapRowStyle = { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: 13 };
const kStyle = { color: "#8FA3A3" };
const vStyle = { color: "#EAFDF3", fontWeight: 600, textAlign: "right", flex: 1, marginLeft: 14 };
const cameraBtnStyle = { display: "flex", flexDirection: "column", alignItems: "center", gap: 5, width: "100%", padding: "22px 18px", borderRadius: 14, background: "rgba(0,255,136,0.05)", border: `1.5px dashed rgba(0,255,136,0.45)`, color: "#EAFDF3", fontSize: 14, fontWeight: 700, cursor: "pointer" };
const extraSlotBtnStyle = { display: "flex", flexDirection: "column", alignItems: "center", gap: 3, width: "100%", padding: "12px 6px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.18)", color: "#8FA3A3", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const photoPreviewStyle = { width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 12, border: `1px solid rgba(0,255,136,0.4)`, display: "block" };
const proofBadgeStyle = { position: "absolute", top: 8, left: 8, background: "rgba(0,255,136,0.9)", color: "#04150D", fontSize: 10, fontWeight: 900, padding: "3px 10px", borderRadius: 999, letterSpacing: 0.4 };
const photoRetakeStyle = { position: "absolute", bottom: 8, right: 8, background: "rgba(5,9,11,0.85)", color: "#EAFDF3", border: "1px solid rgba(0,255,136,0.35)", padding: "6px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer", backdropFilter: "blur(6px)" };
const hiddenPinInputStyle = { position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, zIndex: 2, fontSize: 16, background: "transparent", border: "none", outline: "none", color: "transparent", caretColor: "transparent" };
const primaryBtnStyle = { background: `linear-gradient(135deg, #00E67A, ${NEON})`, color: "#04150D", border: "none", padding: "16px 22px", borderRadius: 14, fontWeight: 900, fontSize: 15, cursor: "pointer", boxShadow: "0 6px 24px rgba(0,255,136,0.35)", width: "100%", letterSpacing: 0.2 };
const ctaAltStyle = { color: "#8FA3A3", textDecoration: "none", fontSize: 13, padding: "8px 16px", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999 };
const camOverlayStyle = { position: "fixed", inset: 0, zIndex: 1200, background: "rgba(2,6,8,0.94)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 18, backdropFilter: "blur(8px)" };
const camGhostBtnStyle = { background: "rgba(255,255,255,0.05)", color: "#EAFDF3", border: "1px solid rgba(255,255,255,0.18)", padding: "11px 18px", borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: "pointer" };
const shutterStyle = { width: 68, height: 68, borderRadius: "50%", background: "rgba(0,255,136,0.15)", border: `2.5px solid ${NEON}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 0 24px rgba(0,255,136,0.35)" };
const shutterInnerStyle = { width: 46, height: 46, borderRadius: "50%", background: NEON, display: "block" };
const successOverlayStyle = { position: "fixed", inset: 0, zIndex: 1300, background: "radial-gradient(ellipse at center, #0A1A12 0%, #030808 75%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20 };
